<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { formatPriceNumber } from "~/utils/emiCalculator";

const props = withDefaults(
  defineProps<{
    price?: number | null;
    defaultOpen?: boolean;
    showPill?: boolean;
    collapsible?: boolean;
  }>(),
  {
    price: null,
    defaultOpen: false,
    showPill: false,
    collapsible: true,
  }
);

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
}>();

const modelOpen = defineModel<boolean>("open", { default: false });
const isOpen = computed({
  get: () => (props.collapsible ? modelOpen.value : true),
  set: (val: boolean) => {
    modelOpen.value = val;
    emit("update:open", val);
  },
});

const formattedPrice = computed(() => {
  if (props.price && props.price > 0) {
    return formatPriceNumber(props.price);
  }
  return "30,000"; // Fallback illustrative price if on request
});

// Animation State
const buybackContainerRef = ref<HTMLElement | null>(null);
const isPlaying = ref(true);
const isUnlocked = ref(false);
const currentProgress = ref(0); // 0 to 1
const fillRatio = computed(() => Math.min(1, Math.max(0, currentProgress.value / 0.7)));

const currentMonth = computed(() => {
  return Math.round(fillRatio.value * 24);
});

// Progress ring calculation (radius = 26, circumference = 2 * PI * 26 ≈ 163.36)
const CIRCUMFERENCE = 163.4;
const strokeDashoffset = computed(() => {
  if (fillRatio.value >= 1) return "0";
  return (CIRCUMFERENCE * (1 - fillRatio.value)).toFixed(2);
});

const isRingVisible = computed(() => currentProgress.value > 0.005);
const isCard1Visible = computed(() => currentProgress.value >= 0.02);
const isCard2Visible = computed(() => currentProgress.value >= 0.75);

let rafId = 0;
let loopTimer: any = null;
const DURATION = 3200;
const PAUSE_DURATION = 900;
let startTime = 0;
let prefersReducedMotion = false;

function step(now: number) {
  if (!startTime) startTime = now;
  const elapsed = now - startTime;
  const t = Math.min(1, Math.max(0, elapsed / DURATION));
  currentProgress.value = t;
  isUnlocked.value = t >= 0.7;

  if (!isPlaying.value) return;

  if (t < 1) {
    rafId = requestAnimationFrame(step);
  } else {
    loopTimer = setTimeout(() => {
      if (isPlaying.value) {
        startTime = performance.now();
        rafId = requestAnimationFrame(step);
      }
    }, PAUSE_DURATION);
  }
}

function startAnimation() {
  stopAnimation();
  isPlaying.value = true;
  if (prefersReducedMotion) {
    currentProgress.value = 1;
    isUnlocked.value = true;
    isPlaying.value = false;
    return;
  }
  currentProgress.value = 0;
  isUnlocked.value = false;
  startTime = performance.now();
  rafId = requestAnimationFrame(step);
}

function stopAnimation() {
  cancelAnimationFrame(rafId);
  clearTimeout(loopTimer);
  isPlaying.value = false;
}

function togglePlay() {
  if (isPlaying.value) {
    stopAnimation();
  } else {
    isPlaying.value = true;
    startTime = performance.now() - currentProgress.value * DURATION;
    rafId = requestAnimationFrame(step);
  }
}

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

onMounted(() => {
  prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (props.defaultOpen || !props.collapsible) {
    isOpen.value = true;
  }
  if (isOpen.value) {
    startAnimation();
  }
});

function scrollToPanel() {
  setTimeout(() => {
    if (buybackContainerRef.value && isOpen.value) {
      buybackContainerRef.value.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    }
  }, 200);
}

watch(
  () => isOpen.value,
  (val) => {
    if (val) {
      startAnimation();
      scrollToPanel();
    } else {
      stopAnimation();
      currentProgress.value = 0;
      isUnlocked.value = false;
    }
  }
);

onBeforeUnmount(() => {
  stopAnimation();
});

defineExpose({
  toggleOpen,
  open: () => {
    isOpen.value = true;
  },
  close: () => {
    isOpen.value = false;
  },
  scrollToPanel,
});
</script>

<template>
  <div class="w-full flex flex-col">
    <!-- Optional Embedded Trigger Pill Button -->
    <button
      v-if="showPill"
      type="button"
      @click="toggleOpen"
      :aria-expanded="isOpen"
      aria-controls="buyback-details-panel"
      class="group inline-flex items-center gap-2 self-start border border-[#bfd2e0] bg-[#e6edf3] text-[#2c4f6d] hover:bg-[#dbe6ee] px-3.5 py-1.5 rounded-[6px] text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer select-none ring-offset-1 focus-visible:outline-2 focus-visible:outline-[#3f6c93] buyback-pill-anim"
    >
      <Icon name="material-symbols:cycle" class="size-4 shrink-0 text-[#3f6c93]" />
      <span>Buyback protected</span>
      <Icon
        name="material-symbols:keyboard-arrow-down-rounded"
        class="size-4 shrink-0 transition-transform duration-300 ease-out"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Collapsible Container (Hidden until opened) -->
    <div
      ref="buybackContainerRef"
      id="buyback-details-panel"
      class="bb-accordion-wrap scroll-mt-24 md:scroll-mt-28"
      :class="{ 'is-open': !collapsible || isOpen }"
    >
      <div class="bb-accordion-inner">
        <div class="border border-button-secondary-hover bg-button-secondary p-5 sm:p-6 md:py-6 md:px-8 rounded-xl shadow-xs">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <!-- Left info column -->
            <div class="lg:col-span-5 flex flex-col justify-center gap-2">
              <div class="flex items-center gap-2.5 text-on-primary-heading">
                <Icon name="material-symbols:cycle" class="size-6 shrink-0 text-[#3f6c93]" />
                <h3 class="text-xl sm:text-2xl font-bold tracking-tight">
                  Buyback program
                </h3>
              </div>

              <p class="text-base sm:text-lg font-semibold text-on-primary-heading mt-0.5">
                What you pay is your floor.
              </p>

              <p class="text-sm sm:text-[15px] text-on-primary-body leading-relaxed mt-1">
                We offer to buy back an artwork you purchase from us after a minimum holding period of 24 months. The buyback value will not be less than the original purchase price, provided the work is returned undamaged.
              </p>
            </div>

            <!-- Right interactive animation column -->
            <div class="lg:col-span-7 bg-white border border-button-secondary-hover/80 rounded-xl p-4 sm:p-5 shadow-xs flex flex-col justify-between">
              <!-- Animation Stage Cards & Ring -->
              <div class="grid grid-cols-3 items-center min-h-[105px] sm:min-h-[115px] relative">
                <!-- Card 1: You Pay -->
                <div
                  class="flex flex-col items-center text-center transition-all duration-500 ease-out transform"
                  :class="[
                    isCard1Visible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-2'
                  ]"
                >
                  <span class="text-[11px] sm:text-xs font-medium text-on-primary-body mb-0.5">
                    You pay
                  </span>
                  <span class="text-lg sm:text-2xl font-bold text-on-primary-heading tracking-tight font-numeric">
                    ₹{{ formattedPrice }}
                  </span>
                </div>

                <!-- Middle: Holding Ring & Timer -->
                <div class="flex flex-col items-center justify-center gap-1.5">
                  <div class="relative size-14 sm:size-16 flex items-center justify-center">
                    <!-- SVG Ring -->
                    <svg viewBox="0 0 64 64" class="size-full -rotate-90 block" aria-hidden="true">
                      <circle
                        cx="32"
                        cy="32"
                        r="26"
                        fill="none"
                        stroke="var(--color-button-secondary-hover, #bfd2e0)"
                        stroke-width="5.5"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="26"
                        fill="none"
                        stroke="#3f6c93"
                        stroke-width="5.5"
                        stroke-linecap="round"
                        :stroke-dasharray="CIRCUMFERENCE"
                        :stroke-dashoffset="strokeDashoffset"
                        :style="{ opacity: isRingVisible ? 1 : 0 }"
                        class="transition-opacity duration-150"
                      />
                    </svg>

                    <!-- Center Lock Icon with morph -->
                    <div class="absolute inset-0 flex items-center justify-center pointer-events-none text-[#2c4f6d]">
                      <Transition name="lock-fade" mode="out-in">
                        <svg
                          v-if="!isUnlocked"
                          key="locked"
                          class="size-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <rect x="4" y="10" width="16" height="10" rx="2" />
                          <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                        </svg>
                        <svg
                          v-else
                          key="unlocked"
                          class="size-5 text-emerald-600"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <rect x="4" y="10" width="16" height="10" rx="2" />
                          <path d="M7 10V7a5 5 0 0 1 9.6-3.2" />
                        </svg>
                      </Transition>
                    </div>
                  </div>

                  <!-- Month Label -->
                  <span class="text-[11px] sm:text-xs font-medium text-on-primary-body font-numeric">
                    Month {{ currentMonth }} of 24
                  </span>
                </div>

                <!-- Card 2: We Buy Back -->
                <div
                  class="flex flex-col items-center text-center transition-all duration-500 ease-out transform"
                  :class="[
                    isCard2Visible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-2'
                  ]"
                >
                  <span class="text-[11px] sm:text-xs font-medium text-on-primary-body mb-1">
                    We buy back for
                  </span>
                  <span class="text-lg sm:text-2xl font-bold text-on-primary-heading tracking-tight font-numeric">
                    ₹{{ formattedPrice }}
                  </span>
                  <span class="text-xs font-bold text-[#1d9e75] mt-0.5">
                    or more
                  </span>
                </div>
              </div>

              <!-- Bottom Footnote -->
              <p class="text-[11px] sm:text-xs text-on-primary-body/90 text-center mt-2.5 pt-2 border-t border-button-secondary-hover/40 leading-normal">
                Buyback value is never less than the purchase price, if the work is returned undamaged after a minimum 24-month holding period.
              </p>

              <!-- Play / Pause Loop Control -->
              <button
                type="button"
                @click="togglePlay"
                class="mt-1.5 mx-auto inline-flex items-center gap-1.5 text-xs text-on-primary-body hover:text-on-primary-heading transition-colors cursor-pointer py-0.5 px-2 rounded-md hover:bg-button-secondary"
              >
                <Icon
                  :name="isPlaying ? 'material-symbols:pause-rounded' : 'material-symbols:play-arrow-rounded'"
                  class="size-3.5 shrink-0"
                />
                <span>{{ isPlaying ? "Pause" : "Play" }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-numeric {
  font-variant-numeric: tabular-nums;
}

/* Accordion Smooth Expand/Collapse */
.bb-accordion-wrap {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  margin-top: 0;
  transition: grid-template-rows 450ms cubic-bezier(0.16, 1, 0.3, 1),
              opacity 350ms ease,
              margin-top 450ms cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: none;
}

.bb-accordion-wrap.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
  margin-top: 1rem;
  pointer-events: auto;
}

.bb-accordion-inner {
  overflow: hidden;
}

.lock-fade-enter-active,
.lock-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.lock-fade-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.lock-fade-leave-to {
  opacity: 0;
  transform: scale(1.1);
}

@keyframes ringPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(63, 108, 147, 0.4);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(63, 108, 147, 0);
  }
}

.animate-pulse-subtle {
  animation: ringPulse 1.6s ease-out 1 0.6s;
}

@media (prefers-reduced-motion: reduce) {
  .animate-pulse-subtle {
    animation: none;
  }
  .bb-accordion-wrap {
    transition: none !important;
  }
}
</style>
